/**
 * The Geschicktendichte-Index (brief §8) and the place vibe (brief §9a.3).
 *
 * Two numbers per cell that deliberately mean different things. The GDI says how much has
 * been going on lately and decays with a seven-day half-life; the vibe says whether the place
 * itself is any good and decays over thirty. A packed platform can be dense and awful at the
 * same time, and merging them into one score would lose exactly the comparison the app is
 * about.
 *
 * Pure and time-injected: `now` is a parameter everywhere, never `Date.now()`. That is what
 * lets the alert path in the browser recompute a fresher number from a cached score without
 * asking the server, and what lets the tests state a scenario instead of sleeping through one.
 */

import type { Attribute, Ratings } from './attributes.ts';
import { quality } from './attributes.ts';
import type { Tally } from './confirm.ts';
import { trustWeight } from './confirm.ts';

const DAY = 24 * 60 * 60 * 1000;

/** Brief §8, and none of these is a knob to turn without amending the brief. */
export const HALF_LIFE_MS = 7 * DAY;
export const K = 6.0;
export const DEDUPE_WINDOW_MS = 3 * 60 * 60 * 1000;
export const MIN_SIGHTINGS = 2;
export const CUTOFF_MS = 28 * DAY;

/** Brief §9a.3. */
export const VIBE_HALF_LIFE_MS = 30 * DAY;
export const VIBE_V = 4.0;
export const VIBE_MIN_VOTES = 3;

/**
 * How far back the vibe reads votes.
 *
 * Not in the brief, because the brief gives the half-life and leaves the query to us: at six
 * half-lives a vote is worth 1.5% of a fresh one, which is below the resolution of the number
 * it feeds. Anything longer is a slower query for an answer that does not change.
 */
export const VIBE_CUTOFF_MS = 180 * DAY;

/** `w_crowd(s)` — brief §8. */
export const CROWD_WEIGHTS: Record<number, number> = { 1: 1.0, 2: 1.6, 3: 2.2 };

export function crowdWeight(crowd: number): number {
  return CROWD_WEIGHTS[crowd] ?? 1.0;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/** `w_recency(s) = 0.5 ^ (age / HALF_LIFE)`. Never above 1, even if a clock runs fast. */
export function recencyWeight(observedAt: Date, now: Date, halfLifeMs = HALF_LIFE_MS): number {
  const age = now.getTime() - observedAt.getTime();
  if (age <= 0) return 1;
  return 0.5 ** (age / halfLifeMs);
}

export type ScoredSighting = {
  id: string;
  userId: string;
  observedAt: Date;
  crowd: number;
  ratings: Ratings;
  tally: Tally;
};

/** `contrib(s) = quality · w_crowd · w_trust · w_recency`. */
export function contribution(
  sighting: ScoredSighting,
  catalogue: readonly Attribute[],
  now: Date,
): number {
  return (
    quality(sighting.ratings, catalogue) *
    crowdWeight(sighting.crowd) *
    trustWeight(sighting.tally) *
    recencyWeight(sighting.observedAt, now)
  );
}

/**
 * Brief §8's de-duplication: inside `DEDUPE_WINDOW`, one report per (user, cell) counts — the
 * one contributing most.
 *
 * Implemented as a greedy pick: take each of a user's reports in descending contribution and
 * keep it unless it falls within three hours of one already kept. That is deterministic (the
 * id breaks ties), it keeps the strongest report rather than an arbitrary one, and it lets an
 * evening of genuinely separate observations count separately — which a fixed three-hour
 * bucket would not, since two reports either side of a bucket boundary would both survive
 * while two inside one would not.
 *
 * Without the rule one enthusiastic person can push a cell to 100 alone, and the GDI then
 * measures enthusiasm rather than density.
 */
export function dedupe(
  sightings: readonly ScoredSighting[],
  catalogue: readonly Attribute[],
  now: Date,
): ScoredSighting[] {
  const byUser = new Map<string, ScoredSighting[]>();
  for (const sighting of sightings) {
    const list = byUser.get(sighting.userId);
    if (list === undefined) byUser.set(sighting.userId, [sighting]);
    else list.push(sighting);
  }

  const kept: ScoredSighting[] = [];
  for (const list of byUser.values()) {
    const ranked = [...list].sort((a, b) => {
      const difference = contribution(b, catalogue, now) - contribution(a, catalogue, now);
      return difference !== 0 ? difference : a.id.localeCompare(b.id);
    });
    const accepted: ScoredSighting[] = [];
    for (const candidate of ranked) {
      const crowded = accepted.some(
        (other) =>
          Math.abs(other.observedAt.getTime() - candidate.observedAt.getTime()) < DEDUPE_WINDOW_MS,
      );
      if (!crowded) accepted.push(candidate);
    }
    kept.push(...accepted);
  }
  return kept;
}

export type PassbyVote = { userId: string; vote: 1 | -1; createdAt: Date };

/**
 * `w_cell(c)` — the pass-by swipes, weakly weighted and tightly clamped (brief §8, §9a.3).
 *
 * The clamp is the whole design: a place everybody thinks is dead loses about a third of its
 * GDI, and it cannot lose more than that, because the reports did happen. A swipe is an
 * opinion about the location; a report is an observation of a crowd.
 */
export function cellWeight(votes: readonly PassbyVote[], now: Date): number {
  let up = 0;
  let down = 0;
  for (const vote of votes) {
    const weight = recencyWeight(vote.createdAt, now, VIBE_HALF_LIFE_MS);
    if (vote.vote === 1) up += weight;
    else down += weight;
  }
  return clamp(1 + 0.06 * up - 0.1 * down, 0.6, 1.3);
}

/** `GDI(c) = round(100 · (1 − e^(−raw / K)))`. */
export function gdiFrom(raw: number): number {
  if (raw <= 0) return 0;
  return Math.round(100 * (1 - Math.exp(-raw / K)));
}

export type Vibe = { vibe: number | null; votes: number };

/**
 * `vibe(c) = round(100 · tanh(vibe_raw / V))`, or `null` below `VIBE_MIN_VOTES`.
 *
 * `null` and `0` are different answers and the UI shows different things for them: nobody has
 * said anything yet, versus opinion is evenly split.
 */
export function vibeFrom(votes: readonly PassbyVote[], now: Date): Vibe {
  const within = votes.filter((vote) => now.getTime() - vote.createdAt.getTime() <= VIBE_CUTOFF_MS);
  if (within.length < VIBE_MIN_VOTES) return { vibe: null, votes: within.length };

  let raw = 0;
  for (const vote of within) {
    raw += vote.vote * recencyWeight(vote.createdAt, now, VIBE_HALF_LIFE_MS);
  }
  return { vibe: Math.round(100 * Math.tanh(raw / VIBE_V)), votes: within.length };
}

export type CellScore = {
  raw: number;
  gdi: number;
  wCell: number;
  vibe: number | null;
  vibeVotes: number;
  sightingCount: number;
  /** The star attribute this cell scores highest on, for the label on the hex. */
  topAttribute: string | null;
  /** Mean per `scale` attribute, for the map's facet filters (brief §7.1). */
  facets: Record<string, number>;
  lastSightingAt: Date | null;
};

export const EMPTY_SCORE: CellScore = {
  raw: 0,
  gdi: 0,
  wCell: 1,
  vibe: null,
  vibeVotes: 0,
  sightingCount: 0,
  topAttribute: null,
  facets: {},
  lastSightingAt: null,
};

/**
 * Everything one cell's row in `cell_scores` holds, from its reports and its swipes.
 *
 * The caller is responsible for having excluded hidden reports and anything past `CUTOFF`,
 * and for having dropped the votes §9a.4 disqualifies — this function scores what it is
 * given. Keeping the filters in the query is what makes the recompute one round trip.
 */
export function scoreCell(
  sightings: readonly ScoredSighting[],
  votes: readonly PassbyVote[],
  catalogue: readonly Attribute[],
  now: Date,
): CellScore {
  if (sightings.length === 0) {
    // A cell can have swipes and no live reports — everything aged past the cutoff, or the
    // reports were disputed away. The vibe survives that; the density does not.
    const mood = vibeFrom(votes, now);
    return {
      ...EMPTY_SCORE,
      vibe: mood.vibe,
      vibeVotes: mood.votes,
      wCell: cellWeight(votes, now),
    };
  }

  const counted = dedupe(sightings, catalogue, now);
  const wCell = cellWeight(votes, now);
  const raw = wCell * counted.reduce((sum, s) => sum + contribution(s, catalogue, now), 0);

  // Attribute means are taken over every report in the window, de-duplicated or not: they
  // describe the crowd, and a report suppressed for anti-spam still saw what it saw.
  const totals = new Map<string, { sum: number; count: number }>();
  for (const sighting of sightings) {
    for (const [key, value] of Object.entries(sighting.ratings)) {
      const entry = totals.get(key) ?? { sum: 0, count: 0 };
      entry.sum += value;
      entry.count += 1;
      totals.set(key, entry);
    }
  }

  const stars = new Set(
    catalogue.filter((attribute) => attribute.kind === 'stars').map((attribute) => attribute.key),
  );
  let topAttribute: string | null = null;
  let topMean = -1;
  for (const [key, entry] of totals) {
    if (!stars.has(key)) continue;
    const mean = entry.sum / entry.count;
    // `>` and not `>=`, with the catalogue order deciding ties: otherwise the label flips
    // between two equally-rated attributes on every recompute for no visible reason.
    if (mean > topMean) {
      topMean = mean;
      topAttribute = key;
    }
  }

  const meanFacets: Record<string, number> = {};
  for (const [key, entry] of totals) {
    if (stars.has(key)) continue;
    meanFacets[key] = Number((entry.sum / entry.count).toFixed(2));
  }

  const lastSightingAt = sightings.reduce<Date>(
    (latest, sighting) => (sighting.observedAt > latest ? sighting.observedAt : latest),
    sightings[0]?.observedAt ?? now,
  );

  const mood = vibeFrom(votes, now);
  return {
    raw,
    gdi: gdiFrom(raw),
    wCell,
    vibe: mood.vibe,
    vibeVotes: mood.votes,
    sightingCount: sightings.length,
    topAttribute,
    facets: meanFacets,
    lastSightingAt,
  };
}

/**
 * Roll several res-9 cells up to their res-7 parent.
 *
 * **Sums `raw`, then applies the curve once.** Averaging the children's GDI would be the
 * wrong arithmetic — the saturation is not linear, so three cells at 63 average to 63 when
 * together they are a raw of 18 and a GDI of 95. Getting this backwards makes the overview
 * zoom systematically calmer than the detail zoom, which reads as a rendering bug.
 */
export function aggregateRaw(raws: readonly number[]): { raw: number; gdi: number } {
  const raw = raws.reduce((sum, value) => sum + value, 0);
  return { raw, gdi: gdiFrom(raw) };
}

/** Whether a cell may be shown to somebody who did not report it (brief §8, `MIN_SIGHTINGS`). */
export function isPublic(sightingCount: number): boolean {
  return sightingCount >= MIN_SIGHTINGS;
}

export type VibeMood = 'hot' | 'meh' | 'lame';

/** Brief §9a.3: `≥ 40` läuft, `−20 … 40` geht so, `< −20` lame. */
export function vibeMood(vibe: number): VibeMood {
  if (vibe >= 40) return 'hot';
  if (vibe < -20) return 'lame';
  return 'meh';
}
